import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { expectArray } from '../composables/expectShape'
import type { ProductOwnerRun } from './product_owner_runs'

const POLL_MS = 60_000

// Header-level health signal for the product-owner agent (#1974): which
// enabled repos are paused or whose latest run failed. Owns its own poll
// (like useFailedJobsStore) because the badge must stay accurate on every
// route, and the full runs store is only loaded by its own view.
//
// A repo the admin turned off reports next_check.state 'disabled' and is
// never counted, so it can't keep the badge lit. An unreachable agent box
// is its own `unreachable` state — never folded into "0 problems".
export const useAgentHealthStore = defineStore('agentHealth', () => {
  const repos = ref<ProductOwnerRun[]>([])
  const unreachable = ref(false)
  let pollTimer: ReturnType<typeof setInterval> | undefined

  const enabledRepos = computed(() => repos.value.filter((r) => r.next_check?.state !== 'disabled'))
  const pausedRepos = computed(() => enabledRepos.value.filter((r) => r.next_check?.state === 'paused'))
  const failedRepos = computed(() =>
    enabledRepos.value.filter((r) => r.status === 'failed' && r.next_check?.state !== 'paused'),
  )
  const problemCount = computed(() => pausedRepos.value.length + failedRepos.value.length)

  const summary = computed(() => {
    const parts: string[] = []
    if (pausedRepos.value.length > 0) parts.push(`${pausedRepos.value.length} paused`)
    if (failedRepos.value.length > 0) parts.push(`${failedRepos.value.length} failed`)
    if (unreachable.value) parts.push('unreachable')
    return parts.join(', ')
  })

  const activityLabel = computed(() => `Product-owner activity${summary.value && ` (${summary.value})`}`)

  async function fetchHealth(): Promise<void> {
    try {
      const res = await fetch('/api/v1/agent/product_owner_runs', { credentials: 'include' })
      if (!res.ok) throw new Error(`Failed to load agent health: ${res.status}`)
      const body = (await res.json()) as { latest_per_repo?: ProductOwnerRun[] }
      repos.value = expectArray<ProductOwnerRun>(body.latest_per_repo ?? [], 'agent health')
      unreachable.value = false
    } catch {
      unreachable.value = true
    }
  }

  function subscribe(): void {
    if (pollTimer) return
    void fetchHealth()
    pollTimer = setInterval(() => void fetchHealth(), POLL_MS)
  }

  function unsubscribe(): void {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = undefined
  }

  return {
    repos,
    unreachable,
    pausedRepos,
    failedRepos,
    problemCount,
    summary,
    activityLabel,
    fetchHealth,
    subscribe,
    unsubscribe,
  }
})
